import { FileSearch, Library, RefreshCw, Search, SearchX } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import {
  stacksServices,
  type StacksResults,
  type StacksService,
  type StacksServiceId,
  type StacksStatus
} from "../../shared/stacks";
import { api, type Session } from "./api";
import { demoStacksRichResults, demoStacksStatus } from "./stacks-demo";
import {
  BackBar,
  Empty,
  fmtInt,
  humanize,
  Notice,
  PageHead,
  PanelEmpty,
  PanelLoading,
  Segmented,
  useIsNarrow,
  useListKeyboard,
  type RowProps,
  useSearchShortcut
} from "./ui";

type Hit = StacksResults["results"][number];
type Evidence = "corroborated" | "partial" | "conflict" | "not_observed";
type Tone = "ok" | "warn" | "bad" | "neutral";

const evidenceTone: Record<Evidence, Tone> = {
  corroborated: "ok",
  partial: "warn",
  conflict: "bad",
  not_observed: "neutral"
};
const evidenceLabel: Record<Evidence, string> = {
  corroborated: "Corroborated",
  partial: "Partial",
  conflict: "Conflict",
  not_observed: "Not observed"
};
// Identifiers first, then the bibliographic fields in citation order.
const fieldOrder = [
  "doi",
  "isbn",
  "pmid",
  "pmcid",
  "title",
  "authors",
  "year",
  "venue",
  "volume",
  "issue",
  "first_page"
];
const fieldLabel: Record<string, string> = {
  doi: "DOI",
  isbn: "ISBN",
  pmid: "PMID",
  pmcid: "PMCID",
  title: "Title",
  authors: "Authors",
  year: "Year",
  venue: "Venue",
  volume: "Volume",
  issue: "Issue",
  first_page: "First page"
};
const limits = [1, 5, 10] as const;

// Operate mode: one citation in, ranked records out, and for each record the
// engine's own field-by-field comparison. The console never reinterprets the
// engine score as confidence and never polls, wakes or stops a service.
export function StacksView({ session, demo }: { session: Session; demo: boolean }) {
  const [services, setServices] = useState<StacksService[]>();
  const [selected, setSelected] = useState<StacksServiceId>("scholarly");
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const abort = new AbortController();
    setError("");
    void (
      demo
        ? Promise.resolve({ services: stacksServices.map((s) => ({ ...s, configured: true })) })
        : api<{ services: StacksService[] }>("/api/stacks", session, { signal: abort.signal })
    )
      .then((value) => {
        if (!abort.signal.aborted) setServices(value.services);
      })
      .catch(() => {
        if (!abort.signal.aborted) setError("Stacks connections could not be loaded.");
      });
    return () => abort.abort();
  }, [session, demo, attempt]);
  const service = services?.find((s) => s.id === selected);
  return (
    <section className="view stacks-view">
      <PageHead title="Stacks" authority="Search services · read only" authorityMode="read">
        <Segmented
          label="Stacks service"
          value={selected}
          options={(services ?? stacksServices).map((s) => ({ value: s.id, label: s.name }))}
          onChange={setSelected}
        />
      </PageHead>
      {error ? (
        <Notice
          tone="error"
          className="toolbar-notice"
          action={{ label: "Retry connections", onClick: () => setAttempt((a) => a + 1) }}
        >
          {error}
        </Notice>
      ) : !services ? (
        <PanelLoading />
      ) : (
        service && (
          // Keyed so switching services clears results and cancels pending requests.
          <ServiceWorkspace key={service.id} service={service} session={session} demo={demo} />
        )
      )}
    </section>
  );
}

function ServiceWorkspace({
  service,
  session,
  demo
}: {
  service: StacksService;
  session: Session;
  demo: boolean;
}) {
  const [status, setStatus] = useState<StacksStatus>();
  const [results, setResults] = useState<StacksResults>();
  // The Review item view links here with ?q=<case name and year>.
  const [query, setQuery] = useState(() => new URLSearchParams(location.search).get("q") ?? "");
  const [submitted, setSubmitted] = useState("");
  const [limit, setLimit] = useState<(typeof limits)[number]>(5);
  const [busy, setBusy] = useState<"status" | "search">();
  const [error, setError] = useState("");
  const [selectedRank, setSelectedRank] = useState<string>();
  const pending = useRef<AbortController | null>(null);
  const queryRef = useRef<HTMLTextAreaElement>(null);
  const canSearch = session.permissions.includes("stacks.search");
  const narrow = useIsNarrow();
  const lastRank = useRef<string | undefined>(undefined);
  useSearchShortcut(queryRef, { focusOnMount: false });
  useEffect(() => () => pending.current?.abort(), []);

  const rows = useMemo(
    () => (results?.results ?? []).map((hit, index) => ({ id: String(index + 1), hit })),
    [results]
  );
  const selectedRow = rows.find((row) => row.id === selectedRank);
  const { onKeyDown, rowProps } = useListKeyboard({
    items: rows,
    selectedId: selectedRank,
    onSelect: setSelectedRank
  });

  async function run(action: "status" | "search") {
    if (pending.current) return;
    const abort = new AbortController();
    pending.current = abort;
    setBusy(action);
    setError("");
    if (action === "search") {
      setResults(undefined);
      setSelectedRank(undefined);
      setSubmitted(query.trim());
    }
    try {
      if (action === "status") {
        const value = demo
          ? { ...demoStacksStatus, checkedAt: new Date().toISOString() }
          : await api<StacksStatus>(`/api/stacks/${service.id}/status`, session, {
              signal: abort.signal
            });
        if (!abort.signal.aborted) setStatus(value);
      } else {
        const value = demo
          ? {
              ...demoStacksRichResults,
              results: demoStacksRichResults.results.slice(0, limit)
            }
          : await api<StacksResults>(`/api/stacks/${service.id}/search`, session, {
              method: "POST",
              body: JSON.stringify({ query: query.trim(), limit }),
              signal: abort.signal
            });
        if (!abort.signal.aborted) {
          setResults(value);
          // On a wide screen the strongest record opens beside the list; on a
          // narrow one the list stays in view until a record is chosen.
          const drawer = window.matchMedia("(max-width: 900px)").matches;
          if (!drawer && value.results.length) setSelectedRank("1");
        }
      }
    } catch (cause) {
      if (!abort.signal.aborted)
        setError(cause instanceof Error ? cause.message : "The request failed. Try again.");
    } finally {
      if (!abort.signal.aborted) {
        setBusy(undefined);
        pending.current = null;
      }
    }
  }

  function search(event: FormEvent) {
    event.preventDefault();
    if (query.trim().length >= 3) void run("search");
  }

  function closeRecord() {
    lastRank.current = selectedRank;
    setSelectedRank(undefined);
  }
  useEffect(() => {
    if (selectedRank || !lastRank.current) return;
    document
      .querySelector<HTMLElement>(`.stacks-table tr[data-rank="${lastRank.current}"]`)
      ?.focus({ preventScroll: false });
    lastRank.current = undefined;
  }, [selectedRank]);

  const searchDisabled = !service.configured || Boolean(busy) || query.trim().length < 3;
  const count = results?.results.length ?? 0;

  return (
    <div
      className="stacks-workspace"
      onKeyDown={(event) => {
        if (event.key === "Escape" && selectedRank) closeRecord();
      }}
    >
      <StatusStrip
        service={service}
        status={status}
        checking={busy === "status"}
        disabled={!service.configured || Boolean(busy)}
        onCheck={() => void run("status")}
      />

      {!service.configured && (
        <Notice tone="warn" className="toolbar-notice" quiet>
          {service.name} is not connected to this console. An administrator must configure its
          server-side credentials.
        </Notice>
      )}

      {canSearch ? (
        <form className="stacks-composer" onSubmit={search}>
          <label htmlFor="stacks-query" className="field-label">
            Citation or title
          </label>
          <textarea
            id="stacks-query"
            ref={queryRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) search(event);
            }}
            required
            minLength={3}
            maxLength={2000}
            rows={2}
            placeholder="Paste one reference exactly as it appears, e.g. Author, A. (2025). Title. Journal, 3(2), 10–20."
            disabled={!service.configured}
            readOnly={Boolean(busy)}
            aria-describedby="stacks-query-hint"
          />
          <div className="stacks-composer-foot">
            <p id="stacks-query-hint">
              Sent to {service.name}. Paste only the citation, never document text.
            </p>
            <span className="stacks-limit">
              <span aria-hidden="true">Results</span>
              <Segmented
                label="Result limit"
                value={String(limit)}
                options={limits.map((n) => ({ value: String(n), label: String(n) }))}
                onChange={(next) => setLimit(Number(next) as (typeof limits)[number])}
              />
            </span>
            <button
              className="btn btn-primary"
              disabled={searchDisabled}
              type="submit"
              aria-keyshortcuts="Control+Enter Meta+Enter"
            >
              <Search aria-hidden="true" />
              {busy === "search" ? "Searching…" : "Search Stacks"}
              <kbd className="btn-kbd" aria-hidden="true">
                Ctrl ↵
              </kbd>
            </button>
          </div>
        </form>
      ) : (
        <Notice tone="info" className="toolbar-notice" quiet>
          You have status access only. Citation searches require the Stacks search permission.
        </Notice>
      )}

      {error && (
        <Notice tone="error" className="toolbar-notice">
          {error}
        </Notice>
      )}

      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {busy === "search"
          ? `Waiting for ${service.name}…`
          : results
            ? `${count} ${count === 1 ? "result" : "results"} returned.`
            : ""}
      </p>

      <div className={`split${selectedRow ? " has-selection" : ""}`}>
        <div className="split-main">
          {results && (
            <div className="stacks-results-head">
              <h2>
                {count} {count === 1 ? "result" : "results"}
              </h2>
              <span className="truncate" title={submitted}>
                for “{submitted}”
              </span>
              <span className="num">Engine time {fmtInt(Math.round(results.ms))} ms</span>
            </div>
          )}
          <div className="table-wrap" onKeyDown={onKeyDown}>
            {busy === "search" ? (
              <div className="empty" aria-hidden="true">
                <Search />
                <b>Searching {service.name}…</b>
                <p>Requests stop after 10 seconds.</p>
              </div>
            ) : !results ? (
              <Empty icon={Library} title={`Search ${service.name}`}>
                {canSearch
                  ? "Paste one citation to see the records the engine ranks for it, and how each record compares field by field."
                  : "Check the service status above. Searching requires the Stacks search permission."}
              </Empty>
            ) : count === 0 ? (
              <Empty icon={SearchX} title="No matching records">
                Try a more complete citation or a distinctive title.
              </Empty>
            ) : (
              <ResultsTable rows={rows} narrow={narrow} rowProps={rowProps} />
            )}
          </div>
        </div>

        <aside className="panel" aria-label="Selected record">
          <BackBar label="All results" onBack={closeRecord} />
          {selectedRow ? (
            <RecordPanel
              key={selectedRow.id}
              focusOnOpen={narrow || window.matchMedia("(max-width: 900px)").matches}
              rank={Number(selectedRow.id)}
              hit={selectedRow.hit}
              submitted={submitted}
            />
          ) : busy === "search" ? (
            <PanelEmpty icon={Search} title="Searching…">
              Records appear here once {service.name} responds.
            </PanelEmpty>
          ) : (
            <PanelEmpty icon={FileSearch} title="No record selected">
              {results && count
                ? "Choose a result to compare it with the citation."
                : "Each record's field-by-field comparison with the citation appears here."}
            </PanelEmpty>
          )}
        </aside>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function StatusStrip({
  service,
  status,
  checking,
  disabled,
  onCheck
}: {
  service: StacksService;
  status: StacksStatus | undefined;
  checking: boolean;
  disabled: boolean;
  onCheck: () => void;
}) {
  const state = status ? describeState(status) : undefined;
  return (
    <div className="toolbar stacks-status" aria-label="Service status" role="group">
      <div aria-live="polite" className="stacks-status-line">
        {checking ? (
          <span className="status" data-tone="neutral">
            <i aria-hidden="true" />
            Checking…
          </span>
        ) : status && state ? (
          <>
            <span className="status" data-tone={state.tone}>
              <i aria-hidden="true" />
              <b>{state.label}</b>
            </span>
            {status.meta && (
              <span className="num">{fmtInt(status.meta.num_docs)} indexed records</span>
            )}
            {status.meta && (
              <span className="truncate">
                {status.meta.sources.length
                  ? status.meta.sources.join(", ")
                  : "Sources not reported"}
              </span>
            )}
            {status.meta && status.meta.snapshots.length > 0 && (
              <details className="stacks-snapshots">
                <summary>
                  {status.meta.snapshots.length}{" "}
                  {status.meta.snapshots.length === 1 ? "snapshot" : "snapshots"}
                </summary>
                <ul>
                  {status.meta.snapshots.map((snapshot, i) => (
                    <li key={i} className="mono">
                      {snapshot}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            <span className="cell-faint">
              Checked {new Date(status.checkedAt).toLocaleTimeString()}
            </span>
          </>
        ) : (
          <>
            <span className="status" data-tone="neutral">
              <i aria-hidden="true" />
              Not checked
            </span>
            <span className="cell-faint">
              {service.id === "research"
                ? "Checks run only when requested. This console never wakes or stops Research Stacks."
                : "Checks run only when requested."}
            </span>
          </>
        )}
      </div>
      <button type="button" className="btn btn-default" disabled={disabled} onClick={onCheck}>
        <RefreshCw aria-hidden="true" />
        Check status
      </button>
    </div>
  );
}

function describeState(status: StacksStatus): { label: string; tone: Tone } {
  switch (status.state) {
    case "available":
      return { label: "Responding", tone: "ok" };
    case "ready":
      return { label: "Ready", tone: "ok" };
    case "warming":
      return {
        label: status.warmPercent !== null ? `Warming · ${status.warmPercent}%` : "Warming",
        tone: "warn"
      };
    case "starting":
      return { label: "Starting", tone: "warn" };
    case "asleep":
      return { label: "Asleep", tone: "neutral" };
  }
}

/* -------------------------------------------------------------------------- */

function ResultsTable({
  rows,
  narrow,
  rowProps
}: {
  rows: { id: string; hit: Hit }[];
  narrow: boolean;
  rowProps: RowProps;
}) {
  return (
    <table className="records fixed stacks-table">
      <colgroup>
        {narrow ? (
          <>
            <col style={{ width: 40 }} />
            <col />
            <col style={{ width: "34%" }} />
          </>
        ) : (
          <>
            <col style={{ width: 44 }} />
            <col />
            <col style={{ width: "22%" }} />
            <col style={{ width: "24%" }} />
            <col style={{ width: 76 }} />
          </>
        )}
      </colgroup>
      <thead>
        <tr>
          <th scope="col" className="right">
            #
          </th>
          <th scope="col">Record</th>
          {!narrow && <th scope="col">Source</th>}
          <th scope="col">Evidence</th>
          {!narrow && (
            <th scope="col" className="right" title="Engine score, as returned by the service">
              Score
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {rows.map(({ id, hit }) => {
          const summary = summarize(hit);
          return (
            <tr key={id} data-rank={id} {...rowProps(id)}>
              <td className="right num cell-faint">{id}</td>
              <td>
                <span className="cell-stack stacks-record">
                  <span className="cell-primary truncate">
                    {hit.fields.title || "Untitled record"}
                  </span>
                  <small className="truncate">{byline(hit)}</small>
                </span>
              </td>
              {!narrow && (
                <td className="cell-muted">
                  {hit.fields.venue || hit.fields.source || "Not reported"}
                </td>
              )}
              <td>
                <span className="status" data-tone={summary.tone}>
                  <i aria-hidden="true" />
                  <span className="stacks-evidence">{summary.label}</span>
                </span>
              </td>
              {!narrow && <td className="right num">{hit.score.toFixed(3)}</td>}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** The engine's strongest signal for a record, in words: any conflict first,
 *  then partial agreement, then corroboration. */
function summarize(hit: Hit): { label: string; tone: Tone } {
  const entries = Object.entries(hit.comparison ?? {}).sort(([a], [b]) => order(a) - order(b));
  if (!entries.length)
    return Object.keys(hit.agreement).length
      ? { label: "Agreement only", tone: "neutral" }
      : { label: "No field comparison", tone: "neutral" };
  const named = (status: Evidence) =>
    entries.filter(([, value]) => value.status === status).map(([field]) => fieldName(field));
  const conflicts = named("conflict");
  if (conflicts.length) return { label: `Conflict · ${conflicts.join(", ")}`, tone: "bad" };
  const partial = named("partial");
  if (partial.length) return { label: `Partial · ${partial.join(", ")}`, tone: "warn" };
  const corroborated = named("corroborated").length;
  if (corroborated)
    return {
      label: `Corroborated · ${corroborated} ${corroborated === 1 ? "field" : "fields"}`,
      tone: "ok"
    };
  return { label: "Not observed", tone: "neutral" };
}

function byline(hit: Hit) {
  const authors = hit.fields.authors ?? [];
  const names =
    authors.length > 3 ? `${authors.slice(0, 3).join(", ")} et al.` : authors.join(", ");
  return [names || "Authors not reported", hit.fields.year].filter(Boolean).join(" · ");
}

/* -------------------------------------------------------------------------- */

function RecordPanel({
  rank,
  hit,
  submitted,
  focusOnOpen
}: {
  rank: number;
  hit: Hit;
  submitted: string;
  focusOnOpen: boolean;
}) {
  const { fields } = hit;
  const comparison = hit.comparison ?? {};
  const headingRef = useRef<HTMLHeadingElement>(null);
  // On a narrow screen the list disappears, so focus follows the record.
  useEffect(() => {
    if (focusOnOpen) headingRef.current?.focus();
  }, [focusOnOpen]);
  const compared = [...new Set([...Object.keys(comparison), ...Object.keys(hit.agreement)])].sort(
    (a, b) => order(a) - order(b)
  );
  const anyMarked = Object.values(comparison).some((value) => value.unmatched_tokens.length > 0);
  const identifiers: [string, ReactNode][] = [];
  if (fields.doi) {
    const doi = fields.doi.replace(/^(?:https?:\/\/)?(?:dx\.)?doi\.org\//i, "");
    identifiers.push([
      "DOI",
      <a
        className="mono"
        href={`https://doi.org/${doi.split("/").map(encodeURIComponent).join("/")}`}
        target="_blank"
        rel="noreferrer"
      >
        {doi}
      </a>
    ]);
  }
  for (const [label, value] of [
    ["ISBN", fields.isbn],
    ["PMID", fields.pmid],
    ["PMCID", fields.pmcid],
    ["Source record", fields.source_id]
  ] as const)
    if (value) identifiers.push([label, <span className="mono">{value}</span>]);

  return (
    <div className="panel-scroll">
      <header className="panel-head">
        <div>
          <h2 ref={headingRef} tabIndex={-1}>
            {fields.title || "Untitled record"}
          </h2>
          <p>{byline(hit)}</p>
          <span className="stacks-rank num">
            Rank {rank} · engine score {hit.score.toFixed(3)} · BM25 {hit.bm25.toFixed(1)}
          </span>
        </div>
      </header>

      <section className="panel-section">
        <h3>Field comparison</h3>
        {compared.length ? (
          <>
            <table className="ledger stacks-compare">
              <thead>
                <tr>
                  <th scope="col">Field</th>
                  <th scope="col">Record</th>
                  <th scope="col">Evidence</th>
                  <th scope="col" className="right" title="The score behind the evidence status">
                    Score
                  </th>
                </tr>
              </thead>
              <tbody>
                {compared.map((field) => {
                  const evidence = comparison[field];
                  // The status is derived from comparison.score; show that one.
                  const score = evidence?.score ?? hit.agreement[field];
                  return (
                    <tr key={field}>
                      <th scope="row">{fieldName(field)}</th>
                      <td className="stacks-value">
                        <RecordValue
                          hit={hit}
                          field={field}
                          unmatched={evidence?.unmatched_tokens ?? []}
                        />
                      </td>
                      <td>
                        {evidence ? (
                          <span className="status" data-tone={evidenceTone[evidence.status]}>
                            <i aria-hidden="true" />
                            {evidenceLabel[evidence.status]}
                          </span>
                        ) : (
                          <span className="cell-faint">Agreement only</span>
                        )}
                      </td>
                      <td className="right num">{score === undefined ? "—" : score.toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {anyMarked && (
              <p className="stacks-legend">
                <mark>Marked</mark> record words do not appear in the citation.
              </p>
            )}
          </>
        ) : (
          <p className="prose">The engine returned no field comparison for this record.</p>
        )}
      </section>

      <section className="panel-section">
        <h3>Citation</h3>
        <p className="stacks-citation">{submitted}</p>
      </section>

      {identifiers.length > 0 && (
        <section className="panel-section">
          <h3>Identifiers</h3>
          <dl className="facts">
            {identifiers.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      <section className="panel-section">
        <details className="stacks-json">
          <summary>Inspect result {rank}</summary>
          <pre>{JSON.stringify(hit, null, 2)}</pre>
        </details>
      </section>
    </div>
  );
}

function order(field: string) {
  const index = fieldOrder.indexOf(field);
  return index < 0 ? fieldOrder.length : index;
}

function fieldName(field: string) {
  return fieldLabel[field] ?? humanize(field);
}

function RecordValue({ hit, field, unmatched }: { hit: Hit; field: string; unmatched: string[] }) {
  // Engine fields the console's response schema does not carry.
  if (!(field in fieldLabel)) return <span className="cell-faint">Not returned to console</span>;
  const f = hit.fields;
  const value =
    field === "authors"
      ? f.authors?.join(", ")
      : field === "year"
        ? f.year?.toString()
        : (f as Record<string, unknown>)[field];
  if (typeof value !== "string" || !value) return <span className="cell-faint">Not on record</span>;
  return <Marked text={value} tokens={unmatched} />;
}

/** Marks the words of a record value that the engine reports as absent from
 *  the citation. Tokenised as the engine does: ASCII-lowercased [a-z0-9]+. */
function Marked({ text, tokens }: { text: string; tokens: string[] }) {
  if (!tokens.length) return <>{text}</>;
  const wanted = new Set(tokens);
  const parts = text.split(/([A-Za-z0-9]+)/);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 && wanted.has(part.toLowerCase()) ? <mark key={i}>{part}</mark> : part
      )}
    </>
  );
}
